import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { FaWhatsapp } from "react-icons/fa";
import { ROUTE_PAGES } from "../data/routeData";
import { useRoutePrices } from "../hooks/useRoutePrices";

const C = {
  bg: "#f8f9fd", dark: "#1a1a2e", gold: "#f5c518", goldD: "#d4a80f",
  gray: "#6b7280", grayL: "#9ca3af", border: "#e5e7eb", white: "#ffffff",
};
const F = { display: "'Playfair Display', serif", body: "'Plus Jakarta Sans', sans-serif" };

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');

  @keyframes fadeUp { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
  @keyframes pulse  { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.4)} }

  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'Plus Jakarta Sans',sans-serif; overflow-x:hidden; }

  .svc-card {
    background:#fff;
    border-radius:24px;
    border:1.5px solid #e5e7eb;
    padding:32px 28px;
    box-shadow:0 6px 24px rgba(0,0,0,.06);
    transition:all .3s ease;
    cursor:default;
  }
  .svc-card:hover {
    transform:translateY(-8px);
    border-color:#f5c518;
    box-shadow:0 20px 56px rgba(245,197,24,.15);
  }
  .svc-card:hover .svc-icon-wrap {
    background:linear-gradient(135deg,#f5c518,#d4a80f) !important;
  }
  .svc-card:hover .svc-icon-wrap span {
    filter: brightness(0) invert(1);
  }

  .feat-card {
    background:#fff;
    border-radius:18px;
    border:1px solid #e5e7eb;
    padding:22px 20px;
    box-shadow:0 4px 16px rgba(0,0,0,.05);
    transition:all .3s;
  }
  .feat-card:hover {
    border-color:#f5c518;
    transform:translateY(-4px);
    box-shadow:0 12px 32px rgba(245,197,24,.12);
  }

  .tag-pill {
    display:inline-flex;
    align-items:center;
    gap:5px;
    font-size:11px;
    font-weight:700;
    background:rgba(245,197,24,.1);
    border:1px solid rgba(245,197,24,.3);
    color:#b8860b;
    border-radius:50px;
    padding:4px 12px;
    letter-spacing:.4px;
  }

  .cta-book:hover {
    transform:translateY(-2px);
    box-shadow:0 12px 36px rgba(245,197,24,.45) !important;
  }

  @media(max-width:768px) {
    .hero-sec  { padding:52px 16px 40px !important; }
    .sec-pad   { padding:52px 16px !important; }
    .grid-2    { grid-template-columns:1fr !important; }
    .grid-3    { grid-template-columns:1fr 1fr !important; }
    .hero-h1   { font-size:clamp(32px,8vw,56px) !important; }
  }
  @media(max-width:480px) {
    .grid-3 { grid-template-columns:1fr !important; }
  }
`;

const SERVICES = [
  {
    icon: "✈️",
    color: "#3b82f6",
    title: "Airport & Railway Transfers",
    desc: "Punctual, stress-free transfers to and from all major airports and railway stations. Pre-book your ride and travel with zero last-minute panic.",
    tags: ["Advance Booking", "24×7 Service"],
    features: ["On-time guarantee", "Flight tracking", "Meet & greet service", "AC vehicles"],
  },
  {
    icon: "🗺️",
    color: "#10b981",
    title: "Outstation Cabs",
    desc: "One-way or round-trip outstation rides across Gujarat and beyond. Experienced drivers, well-maintained vehicles, and a smooth journey guaranteed.",
    tags: ["One-Way", "Round Trip", "Hill Stations"],
    features: ["Flexible pick-up time", "Verified drivers", "All-India coverage", "Transparent pricing"],
  },
  {
    icon: "👑",
    color: "#8b5cf6",
    title: "Luxury Rentals",
    desc: "Arrive in style. Our premium fleet is perfect for weddings, corporate events, film shoots, or any occasion that demands first-class comfort.",
    tags: ["Premium Fleet", "Chauffeur Driven", "Corporate Events"],
    features: ["Innova Crysta / SUV", "Suited chauffeurs", "Customizable routes", "Flexible billing"],
  },
  {
    icon: "🏢",
    color: "#f59e0b",
    title: "Corporate Cab Services",
    desc: "Manage employee transportation effortlessly. Custom packages, monthly billing, and dedicated account managers for businesses of all sizes.",
    tags: ["Monthly Billing", "Fleet Management", "Dedicated Support"],
    features: ["Employee pick-up & drop", "Monthly invoicing", "Dedicated account manager", "GPS tracking"],
  },
  {
    icon: "📍",
    color: "#ef4444",
    title: "Live GPS Tracking",
    desc: "Track every trip in real time. Know your driver's location, ETA, and route — giving you and your family complete peace of mind, always.",
    tags: ["Real-Time GPS", "Live Location", "Safety First"],
    features: ["Live cab tracking", "Share trip with family", "Instant alerts", "24×7 monitoring"],
  },
  {
    icon: "🚐",
    color: "#06b6d4",
    title: "Tempo Traveller / Group Rides",
    desc: "Planning a group trip, pilgrimage, or picnic? Our Tempo Traveller seats up to 12 and ensures everyone travels together comfortably.",
    tags: ["12 Seater", "Group Tours", "Pilgrimage Trips"],
    features: ["Up to 12 passengers", "Large luggage space", "AC comfort", "Experienced driver"],
  },
];

const STATS = [
  { num: "12K+", label: "Happy Rides" },
  { num: "850+", label: "Verified Cabs" },
  { num: "98%",  label: "Satisfaction Rate" },
  { num: "7+",   label: "Years Experience" },
];

const WHY = [
  { icon: "🔒", title: "Zero Hidden Charges",  desc: "Toll, fuel, and driver allowance — all included upfront. No surprises at the end of your trip." },
  { icon: "🧑‍✈️", title: "Background-Verified Drivers", desc: "Every driver is police-verified, trained, and rated by real customers before joining our platform." },
  { icon: "❌", title: "Free Cancellation",     desc: "Plans change. Cancel anytime before departure at zero cost — no questions asked." },
  { icon: "📞", title: "24×7 Customer Support", desc: "Call, WhatsApp, or chat with our support team any time of day or night." },
];

export default function ServicesPage() {
  const [activeCard, setActiveCard] = useState(null);
  const livePrices = useRoutePrices(ROUTE_PAGES);

  return (
    <>
      <style>{CSS}</style>
      <div style={{ fontFamily: F.body, background: C.bg, color: C.dark, overflowX: "hidden" }}>
        <Header defaultActive="Services" />

        {/* ── HERO ── */}
        <div className="hero-sec" style={{ background: "linear-gradient(150deg,#f8f9fd 0%,#fffbea 55%,#f8f9fd 100%)", padding: "72px 24px 56px", textAlign: "center", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -60, right: -60, width: 280, height: 280, borderRadius: "50%", background: "rgba(245,197,24,.07)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", bottom: -40, left: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(26,26,46,.04)", pointerEvents: "none" }} />

          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(245,197,24,.1)", border: "1px solid rgba(245,197,24,.3)", borderRadius: 50, padding: "6px 16px", fontSize: 11, color: "#b8860b", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 20 }}>
            <span style={{ width: 6, height: 6, background: C.gold, borderRadius: "50%", animation: "pulse 1.8s ease-in-out infinite" }} />
            What We Offer
          </div>

          <h1 className="hero-h1" style={{ fontFamily: F.display, fontSize: "clamp(36px,6vw,68px)", fontWeight: 900, color: C.dark, letterSpacing: "-2px", lineHeight: 1.1, marginBottom: 16, animation: "fadeUp .7s ease both" }}>
            Every Ride, <span style={{ color: C.gold }}>Every Need</span>
          </h1>
          <p style={{ fontSize: "clamp(14px,1.8vw,17px)", color: C.gray, maxWidth: 520, margin: "0 auto 32px", lineHeight: 1.7, animation: "fadeUp .8s .1s ease both" }}>
            From airport transfers to luxury outstation trips — HaloSorath covers it all with verified drivers, transparent pricing, and zero surprises.
          </p>

          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="/" style={{ textDecoration: "none" }}>
              <button className="cta-book" style={{ background: `linear-gradient(135deg,${C.gold},${C.goldD})`, color: C.dark, border: "none", borderRadius: 50, padding: "14px 36px", fontSize: 15, fontWeight: 800, fontFamily: F.display, cursor: "pointer", boxShadow: "0 6px 28px rgba(245,197,24,.35)", transition: "all .3s" }}>
                🚗 Book a Ride
              </button>
            </a>
            <a href="https://wa.me/919265407669" target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
              <button style={{ background: "#25D366", color: "#fff", border: "none", borderRadius: 50, padding: "14px 36px", fontSize: 15, fontWeight: 700, fontFamily: F.body, cursor: "pointer", transition: "all .3s" }}>
                💬 WhatsApp Us
              </button>
            </a>
          </div>
        </div>

        {/* ── STATS BAR ── */}
        <div style={{ background: C.dark, padding: "28px 24px" }}>
          <div style={{ maxWidth: 900, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 20, textAlign: "center" }}>
            {STATS.map((s, i) => (
              <div key={i}>
                <div style={{ fontFamily: F.display, fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: C.gold, lineHeight: 1 }}>{s.num}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)", letterSpacing: "1.5px", textTransform: "uppercase", fontWeight: 600, marginTop: 6 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── SERVICES GRID ── */}
        <section className="sec-pad" style={{ padding: "80px 24px" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 52 }}>
              <p style={{ fontSize: 11, letterSpacing: "2.5px", textTransform: "uppercase", fontWeight: 700, color: C.goldD, marginBottom: 10 }}>Our Services</p>
              <h2 style={{ fontFamily: F.display, fontSize: "clamp(26px,4vw,44px)", fontWeight: 800, color: C.dark, lineHeight: 1.15, marginBottom: 14 }}>Everything You Need,<br />One Platform</h2>
              <p style={{ fontSize: "clamp(13px,1.7vw,16px)", color: C.grayL, maxWidth: 480, margin: "0 auto" }}>From daily commutes to outstation adventures — we've got your ride covered.</p>
            </div>

            <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 24 }}>
              {SERVICES.map((s, i) => (
                <div key={i} className="svc-card" onClick={() => setActiveCard(activeCard === i ? null : i)}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 18 }}>
                    <div className="svc-icon-wrap" style={{ width: 56, height: 56, minWidth: 56, borderRadius: 16, background: s.color + "15", border: `1.5px solid ${s.color}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, transition: "background .3s" }}>
                      <span>{s.icon}</span>
                    </div>
                    <div>
                      <h3 style={{ fontFamily: F.display, fontSize: "clamp(16px,2vw,20px)", fontWeight: 800, color: C.dark, marginBottom: 4 }}>{s.title}</h3>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {s.tags.map(t => <span key={t} className="tag-pill">{t}</span>)}
                      </div>
                    </div>
                  </div>

                  <p style={{ fontSize: 13.5, color: C.gray, lineHeight: 1.75, marginBottom: 18 }}>{s.desc}</p>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    {s.features.map((f, j) => (
                      <div key={j} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "#374151" }}>
                        <span style={{ width: 18, height: 18, borderRadius: "50%", background: "rgba(34,197,94,.1)", border: "1px solid rgba(34,197,94,.25)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#16a34a", flexShrink: 0 }}>✔</span>
                        {f}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WHY HaloSorath ── */}
        {/* <section className="sec-pad" style={{ background: "#f0f2f8", padding: "72px 24px" }}>
          <div style={{ maxWidth: 1000, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 48 }}>
              <p style={{ fontSize: 11, letterSpacing: "2.5px", textTransform: "uppercase", fontWeight: 700, color: C.goldD, marginBottom: 10 }}>Why Choose Us</p>
              <h2 style={{ fontFamily: F.display, fontSize: "clamp(24px,4vw,40px)", fontWeight: 800, color: C.dark }}>Rides You Can Actually Trust</h2>
            </div>
            <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 16 }}>
              {WHY.map((w, i) => (
                <div key={i} className="feat-card">
                  <div style={{ width: 46, height: 46, borderRadius: 13, background: "rgba(245,197,24,.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, marginBottom: 14 }}>{w.icon}</div>
                  <h4 style={{ fontFamily: F.display, fontSize: 16, fontWeight: 800, color: C.dark, marginBottom: 7 }}>{w.title}</h4>
                  <p style={{ fontSize: 13, color: C.grayL, lineHeight: 1.7 }}>{w.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section> */}

        {/* ── CTA BANNER ── */}
        {/* <section style={{ background: `linear-gradient(135deg,${C.gold},${C.goldD})`, padding: "60px 24px", textAlign: "center" }}>
          <h2 style={{ fontFamily: F.display, fontSize: "clamp(24px,4.5vw,46px)", fontWeight: 900, color: C.dark, marginBottom: 12 }}>Ready to Book Your Ride?</h2>
          <p style={{ fontSize: "clamp(13px,1.8vw,17px)", color: "rgba(26,26,46,.65)", maxWidth: 440, margin: "0 auto 28px" }}>No hidden fees. No middlemen. Just great rides across Gujarat.</p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="/" style={{ textDecoration: "none" }}>
              <button className="cta-book" style={{ background: C.dark, color: C.gold, border: "none", borderRadius: 50, padding: "14px 40px", fontSize: 15, fontWeight: 800, fontFamily: F.display, cursor: "pointer", transition: "all .25s" }}>🚗 Book Now</button>
            </a>
            <a href="tel:+919265407669" style={{ textDecoration: "none" }}>
              <button style={{ background: "transparent", color: C.dark, border: `2px solid ${C.dark}`, borderRadius: 50, padding: "14px 40px", fontSize: 15, fontWeight: 700, fontFamily: F.body, cursor: "pointer" }}>📞 Call Us</button>
            </a>
          </div>
        </section> */}

          {/* ── ALL ROUTES ── */}
<section
  className="sec-pad"
  style={{
    padding: "70px 24px",
    background: "#fff",
  }}
>
  <div style={{ maxWidth: 1200, margin: "0 auto" }}>
    
    <div style={{ textAlign: "center", marginBottom: 48 }}>
      <p
        style={{
          fontSize: 11,
          letterSpacing: "2px",
          textTransform: "uppercase",
          fontWeight: 700,
          color: C.goldD,
          marginBottom: 10,
        }}
      >
        Popular Cab Routes
      </p>

      <h2
        style={{
          fontFamily: F.display,
          fontSize: "clamp(28px,4vw,46px)",
          fontWeight: 900,
          color: C.dark,
          marginBottom: 14,
        }}
      >
        Explore All Routes
      </h2>

      <p
        style={{
          color: C.gray,
          fontSize: 15,
          maxWidth: 620,
          margin: "0 auto",
          lineHeight: 1.7,
        }}
      >
        Book affordable one-way and round-trip taxis across Gujarat with verified drivers and transparent pricing.
      </p>
    </div>

    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
        gap: 18,
      }}
    >
      {ROUTE_PAGES.map((route, index) => (
        <a
          key={index}
          href={`/${route.slug}`}
          style={{
            textDecoration: "none",
          }}
        >
          <div
            style={{
              background: "#fff",
              border: "1.5px solid #e5e7eb",
              borderRadius: 20,
              padding: "22px 20px",
              transition: "all .25s ease",
              boxShadow: "0 4px 18px rgba(0,0,0,.05)",
              height: "100%",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-5px)";
              e.currentTarget.style.borderColor = "#f5c518";
              e.currentTarget.style.boxShadow =
                "0 18px 40px rgba(245,197,24,.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.borderColor = "#e5e7eb";
              e.currentTarget.style.boxShadow =
                "0 4px 18px rgba(0,0,0,.05)";
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 14,
                  background: "rgba(245,197,24,.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 22,
                }}
              >
                🚖
              </div>

              <span
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  color: "#16a34a",
                  background: "#f0fdf4",
                  padding: "6px 10px",
                  borderRadius: 999,
                }}
              >
                {livePrices[route.slug] === "loading"
  ? <span style={{
      display: "inline-block", width: 14, height: 14,
      border: "2px solid #16a34a", borderTopColor: "transparent",
      borderRadius: "50%", animation: "spin 0.8s linear infinite",
      verticalAlign: "middle"
    }} />
  : livePrices[route.slug] ?? "₹ --"}
              </span>
            </div>

            <h3
              style={{
                fontFamily: F.display,
                fontSize: 20,
                fontWeight: 800,
                color: C.dark,
                marginBottom: 8,
                lineHeight: 1.3,
              }}
            >
              {route.from} → {route.to}
            </h3>

            <p
              style={{
                fontSize: 13,
                color: C.gray,
                lineHeight: 1.7,
                marginBottom: 16,
              }}
            >
              {route.distance} • {route.duration}
            </p>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginTop: "auto",
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: C.goldD,
                }}
              >
                Book Now
              </span>

              <span
                style={{
                  fontSize: 18,
                  color: C.goldD,
                }}
              >
                →
              </span>
            </div>
          </div>
        </a>
      ))}
    </div>
  </div>
</section>

        <Footer />

       
      </div>
    </>
  );
}